/**
 * Logging utility with timestamps and duration tracking
 * Provides consistent logging format with timestamps, elapsed time, and recording duration
 * Uses Datadog logs for remote environments, console logs for local development
 */
import { datadogLogs } from "@datadog/browser-logs";

import { viteEnv } from "utils/vite-env.util";

const isLocalEnv = viteEnv.VITE_ENV === "local";

// Logging utility with timestamps and duration tracking
export const createLogger = (() => {
  const sessionStartTime = Date.now();
  const getElapsedTime = () => {
    const elapsed = Date.now() - sessionStartTime;
    return `${(elapsed / 1000).toFixed(3)}s`;
  };

  const getTimestamp = () => {
    return new Date().toISOString();
  };

  const getRecordingDuration = (startTime: number | null) => {
    if (!startTime) return "N/A";
    const duration = Date.now() - startTime;
    return `${(duration / 1000).toFixed(3)}s`;
  };

  const buildContext = (startTime?: number | null) => {
    const timestamp = getTimestamp();
    const elapsed = getElapsedTime();
    const recordingDuration = startTime
      ? getRecordingDuration(startTime)
      : null;

    return {
      timestamp,
      elapsed,
      recordingDuration,
      recordingDurationMs: startTime ? Date.now() - startTime : null
    };
  };

  const buildMessage = (message: string, startTime?: number | null) => {
    const timestamp = getTimestamp();
    const elapsed = getElapsedTime();
    const duration = startTime
      ? ` [Recording: ${getRecordingDuration(startTime)}]`
      : "";
    return `[${timestamp}] [${elapsed}]${duration} ${message}`;
  };

  return {
    log: (message: string, startTime?: number | null) => {
      const formattedMessage = buildMessage(message, startTime);
      const context = buildContext(startTime);

      if (isLocalEnv) {
        console.log(formattedMessage);
      } else {
        datadogLogs.logger.log("Ambient Scribe", {
          message,
          ...context
        });
      }
    },
    warn: (message: string, startTime?: number | null) => {
      const formattedMessage = buildMessage(message, startTime);
      const context = buildContext(startTime);

      if (isLocalEnv) {
        console.warn(formattedMessage);
      } else {
        datadogLogs.logger.warn("Ambient Scribe", {
          message,
          ...context
        });
      }
    },
    error: (message: string, error?: unknown, startTime?: number | null) => {
      const formattedMessage = buildMessage(message, startTime);
      const context = buildContext(startTime);

      if (isLocalEnv) {
        console.error(formattedMessage, error || "");
      } else {
        datadogLogs.logger.error(
          "Ambient Scribe",
          {
            message,
            ...context,
            error:
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack
                  }
                : error
          },
          error instanceof Error ? error : undefined
        );
      }
    },
    getRecordingDuration: (startTime: number | null) =>
      getRecordingDuration(startTime)
  };
})();
